import React from 'react'

import { Button } from '@octane/spark'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import { DashboardItem } from 'state/dashboard/types'
import AssetType from 'state/enums/AssetType'
import getNewApplicationPath from 'utils/getNewApplicationPath'
import navigate from 'utils/navigate'

import useUser from '../../hooks/useUser'
import AutoBonusTarget from './AutoBonusTarget'
import {
    QuickActions,
    DashboardContainer,
    DashboardBody,
    UpdatedInfo,
    DashboardCSSVariables,
} from './Dashboard.styled'
import { DashboardProps } from './types'

const quickActionEvent = {
    category: 'Loyalty Dashboard',
    action: 'Quick Action',
}

export default function Dashboard({
    data,
    components,
    dealershipName,
}: Readonly<DashboardProps>) {
    const featureFlags = useFeatureFlagContext()
    const { user, isMultiAssetDealership, assetTypes } = useUser()
    const hasAutoAssetType = assetTypes.includes(AssetType.AUTOMOBILE)

    const newApplicationPath = getNewApplicationPath(
        featureFlags,
        user?.dealership?.primaryAssetType,
    )

    return (
        <DashboardCSSVariables>
            <DashboardContainer>
                {!isMultiAssetDealership && <h2>Welcome, {dealershipName}</h2>}

                <QuickActions>
                    <h3>Quick Actions</h3>
                    <Button
                        onClick={() =>
                            navigate(newApplicationPath, quickActionEvent)
                        }
                    >
                        + New Deal
                    </Button>
                    <Button
                        onClick={() =>
                            navigate('#/saved-applications', quickActionEvent)
                        }
                    >
                        View Active Deals
                    </Button>
                </QuickActions>

                {hasAutoAssetType && <AutoBonusTarget />}

                <DashboardBody>
                    <UpdatedInfo>
                        Updated at{' '}
                        {new Date(
                            data.dashboard.lastUpdated,
                        ).toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </UpdatedInfo>

                    {data.dashboard.items.map(
                        (item: DashboardItem, index: number) => {
                            const Component = components.get(item.component)

                            if (Component) {
                                return (
                                    <Component
                                        key={`${item.component}_${index}`}
                                        componentStyle={item.componentStyle}
                                        data={item.data}
                                        dealershipName={dealershipName}
                                    />
                                )
                            }

                            return <div key={`${item.component}_${index}`} />
                        },
                    )}
                </DashboardBody>
            </DashboardContainer>
        </DashboardCSSVariables>
    )
}
